"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RootState } from "@/store";
import { forgotPassword } from "@/store/slices/authSlice";
import {
  CheckCircle,
  ChevronLeft,
  CircleAlert,
  Loader2,
  Square,
  SquareCheck,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

type FormValues = {
  email: string;
};

export default function ForgotPasswordForm({
  onGoBack,
}: {
  onGoBack: () => void;
}) {
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const error = useSelector((state: RootState) => state.auth.error);
  // Track if form has been interacted with
  const [formInteracted, setFormInteracted] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
  });

  const email = watch("email");

  // Button should be enabled only if email is valid and terms are checked
  const isButtonEnabled = isValid && agreeToTerms && !isLoading;

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);

    try {
      // Call the forgot password action
      const resultAction = await dispatch(forgotPassword(data.email) as any);

      // Check if the action was fulfilled
      if (forgotPassword.fulfilled.match(resultAction)) {
        const responseData = resultAction.payload;
        const id = responseData?.id;

        // Set success state
        setIsSuccess(true);

        // Redirect to OTP page after 2 seconds
        setTimeout(() => {
          navigate(
            `/auth/otp?email=${encodeURIComponent(data.email)}&token=${id}`
          );
        }, 2000);
      }
    } catch (error: any) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl border border-white/20">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Email Sent!
            </h3>
            <p className="text-gray-600 text-sm">
              We've sent a verification code to <strong>{email}</strong>. Please
              check your inbox and follow the instructions.
            </p>
          </div>
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-[#FF6B6B] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">
              Redirecting to verification page...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-[18px] w-full max-w-md shadow-2xl border border-white/20 backdrop-blur-sm">
      <div className="mb-[2.5px] relative z-50">
        <button
          type="button"
          onClick={onGoBack}
          className="text-[#1E1E1E] text-[14px] font-[500] no-underline hover:underline cursor-pointer flex items-center before:text-lg"
        >
          <ChevronLeft />
          Go back
        </button>
      </div>

      <div className="space-y-6 pb-[40px] p-[10px]">
        <div>
          <h3 className="text-center text-xl font-bold text-gray-900 mb-2">
            Forgot Password?
          </h3>
          <p className="text-gray-600 text-sm text-center px-3">
            Please enter your email. We’ll send you a verification code to reset
            your password.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-md font-medium text-gray-700 mb-2"
            >
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Your email address"
              className={`w-full h-12 rounded-xl 
                ${
                  errors.email
                    ? "border-[#FF3B30]"
                    : formInteracted
                    ? "border-gray-200 focus:outline-none focus:border-[#7872C8] focus:ring-[#7872C8]"
                    : "border-gray-200"
                }
               `}
              onFocus={() => setFormInteracted(true)}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Please enter a valid email address",
                },
              })}
            />

            {errors.email && (
              <span className="text-[#FF3B30] text-[12px] font-[500] mt-1 block">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => {
                setAgreeToTerms(!agreeToTerms);
              }}
            >
              {agreeToTerms ? (
                <SquareCheck className="w-6 h-6 text-[#5C88C5]" />
              ) : (
                <Square className="w-6 h-6 text-[#354052]" />
              )}
            </div>

            <label htmlFor="terms" className="text-sm font-bold text-gray-700">
              I agree to{" "}
              <a
                href="https://musictemple.in/terms-of-use/"
                target="_blank"
                className="text-[#7872C8] hover:underline"
              >
                Terms & Condition
              </a>
            </label>
          </div>

          {error && (
            <div className="w-full bg-[#FFE9EB] p-2 rounded-lg">
              <div className="w-full h-full border-l-4 pl-3 border-[#FF152C] flex flex-row items-center justify-start gap-3 rounded-lg">
                <div className="w-[24px] h-[24px]">
                  <CircleAlert className="w-[24px] h-[24px] text-[#FF152C]" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {error}
                </span>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full mt-4 h-12 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-semibold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            disabled={!isButtonEnabled}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "CONTINUE"
            )}
          </Button>
        </form>

        {/* <div className="text-center">
          <p className="text-sm text-gray-600">
            Remember your password?{" "}
            <Link href="/auth/signin" className="text-[#5D88C6] hover:underline font-medium">
              Back to Sign In
            </Link>
          </p>
        </div> */}
      </div>
    </div>
  );
}
